<script lang="ts">
    import TextInput     from '../components/forms/TextInput.svelte';
    import Textarea      from '../components/forms/Textarea.svelte';
    import Select        from '../components/forms/Select.svelte';
    import ToggleInput   from '../components/forms/ToggleInput.svelte';
    import Checkbox      from '../components/forms/Checkbox.svelte';
    import RadioGroup    from '../components/forms/RadioGroup.svelte';
    import RangeInput    from '../components/forms/RangeInput.svelte';
    import FileInput     from '../components/forms/FileInput.svelte';
    import ColorInput    from '../components/forms/ColorInput.svelte';
    import IPInput       from '../components/forms/IPInput.svelte';
    import NetmaskSelect from '../components/forms/NetmaskSelect.svelte';
    import MacInput      from '../components/forms/MacInput.svelte';
    import HostnameInput from '../components/forms/HostnameInput.svelte';
    import PortInput     from '../components/forms/PortInput.svelte';
    import NumberStepper from '../components/forms/NumberStepper.svelte';
    import PasswordInput from '../components/forms/PasswordInput.svelte';
    import DurationInput from '../components/forms/DurationInput.svelte';
    import PinInput      from '../components/forms/PinInput.svelte';
    import TagsInput     from '../components/forms/TagsInput.svelte';
    import ComboBox      from '../components/forms/ComboBox.svelte';
    import Button        from '../components/forms/Button.svelte';
    import SearchInput   from '../components/forms/SearchInput.svelte';
    import FilterChips   from '../components/forms/FilterChips.svelte';

    let errors: Record<string, string[]> = {};

    $: errorCount = Object.values(errors).filter(e => e.length > 0).length;

    const portOptions = [
        { value: '/dev/ttyS0',   label: '/dev/ttyS0'   },
        { value: '/dev/ttyS1',   label: '/dev/ttyS1'   },
        { value: '/dev/ttyUSB0', label: '/dev/ttyUSB0' },
        { value: '/dev/ttyUSB1', label: '/dev/ttyUSB1' },
        { value: 'COM1', label: 'COM1' },
        { value: 'COM2', label: 'COM2' },
        { value: 'COM3', label: 'COM3' },
    ];

    const baudRates = [9600, 19200, 38400, 57600, 115200, 230400, 460800, 921600];

    let baudIndex: number = 4; // defaults to 115200
    $: baudLabel = baudRates[baudIndex].toLocaleString() + ' bps';

    const selectOptions = [
        { value: 'a', label: 'Option A' },
        { value: 'b', label: 'Option B' },
        { value: 'c', label: 'Option C' },
        { value: 'd', label: 'Option D' },
    ];

    const comboOptions = [
        { value: 'debian', label: 'Debian' },
        { value: 'alpine', label: 'Alpine Linux' },
        { value: 'ubuntu', label: 'Ubuntu' },
        { value: 'fedora', label: 'Fedora' },
        { value: 'arch',   label: 'Arch Linux' },
        { value: 'nixos',  label: 'NixOS' },
    ];

    let allowedHosts: string[] = ['192.168.1.1', 'router.local'];
    let password: string = '';
    let searchValue: string = '';

    const pillOptions = (['error', 'warning', 'info', 'success'] as const).map(v => ({ value: v, label: v }));
    type PillType = 'error' | 'warning' | 'info' | 'success';
    let activePills = new Set<PillType>();
    const togglePill = (v: string) => {
        const p = v as PillType;
        activePills.has(p) ? activePills.delete(p) : activePills.add(p);
        activePills = activePills;
    };

    const radioOptions = [
        { value: 'a', label: 'Option A' },
        { value: 'b', label: 'Option B' },
        { value: 'c', label: 'Option C' },
    ];
</script>

<div class="page">
    <h1>Form Elements</h1>

    {#if errorCount > 0}
        <div class="validation-banner">
            {errorCount} field{errorCount !== 1 ? 's' : ''} with errors
        </div>
    {/if}

    <!-- ── Text inputs ────────────────────────────────────────────────────── -->
    <section class="card">
        <h2>Text inputs</h2>
        <div class="field-grid">
            <TextInput {errors} name="f-text"     label="Text"     />
            <TextInput {errors} name="f-email"    label="Email"    type="email"    placeholder="user@example.com" />
            <TextInput {errors} name="f-password" label="Password" type="password" placeholder="••••••••" />
            <TextInput {errors} name="f-number"   label="Number"   type="number"   placeholder="0" />
            <TextInput {errors} name="f-url"      label="URL"      type="url"      placeholder="https://example.com" />
            <TextInput {errors} name="f-tel"      label="Phone"    type="tel"      placeholder="+1 000 000 0000" />
            <TextInput {errors} name="f-date"     label="Date"     type="date" />
            <TextInput {errors} name="f-time"     label="Time"     type="time" />
            <TextInput {errors} name="f-req"      label="Required" required placeholder="Cannot be empty" />
            <TextInput {errors} name="f-disabled" label="Disabled" disabled placeholder="Not editable" />
        </div>
    </section>

    <!-- ── Textarea ───────────────────────────────────────────────────────── -->
    <section class="card">
        <h2>Textarea</h2>
        <Textarea {errors} name="f-textarea" label="Message" placeholder="Write something…" />
    </section>

    <!-- ── Select ─────────────────────────────────────────────────────────── -->
    <section class="card">
        <h2>Select</h2>
        <div class="field-grid">
            <Select {errors} name="f-select"       label="Single"   options={selectOptions} required />
            <Select {errors} name="f-select-multi" label="Multiple" options={selectOptions} multiple />
        </div>
    </section>

    <!-- ── Checkboxes ─────────────────────────────────────────────────────── -->
    <section class="card">
        <h2>Checkboxes</h2>
        <div class="check-group">
            <Checkbox {errors} name="f-check1"    label="Checked"           checked />
            <Checkbox {errors} name="f-check2"    label="Unchecked" />
            <Checkbox {errors} name="f-check3"    label="Required"          required />
            <Checkbox {errors} name="f-check-dis" label="Disabled" disabled checked />
        </div>
    </section>

    <!-- ── Switches ─────────────────────────────────────────────────────────── -->
    <section class="card">
        <h2>Switches</h2>
        <div class="card-body">
            <ToggleInput {errors} name="f-toggle1" label="System Power" description="Main system power toggle" checked />
            <ToggleInput {errors} name="f-toggle2" label="Notifications" description="Receive alerts on mobile" />
            <ToggleInput {errors} name="f-toggle3" label="Maintenance Mode" disabled />
        </div>
    </section>

    <!-- ── Radio buttons ──────────────────────────────────────────────────── -->
    <section class="card">
        <h2>Radio buttons</h2>
        <RadioGroup {errors} name="f-radio" label="Pick one" options={radioOptions} value="a" required />
    </section>

    <!-- ── Pickers ────────────────────────────────────────────────────────── -->
    <section class="card">
        <h2>Pickers</h2>
        <div class="field-grid">
            <RangeInput {errors} name="f-range" label="Volume" value={40} />
            <ColorInput {errors} name="f-color" label="Color"  value="#FFB300" />
            <FileInput  {errors} name="f-file"  label="File" />
        </div>
    </section>

    <!-- ── Network ────────────────────────────────────────────────────────── -->
    <section class="card">
        <h2>Network</h2>
        <div class="field-grid">
            <IPInput      {errors} name="f-ip"      label="IP address" />
            <IPInput      {errors} name="f-gateway" label="Gateway" />
            <NetmaskSelect {errors} name="f-netmask" label="Netmask" />
        </div>
    </section>

    <!-- ── Serial port ────────────────────────────────────────────────────── -->
    <section class="card">
        <h2>Serial port</h2>
        <div class="field-grid">
            <Select {errors} name="f-port" label="Port" options={portOptions} required />
            <RangeInput {errors} name="f-baud" label="Baud rate — {baudLabel}" bind:value={baudIndex} min={0} max={baudRates.length - 1} step={1} showValue={false} />
        </div>
    </section>

    <!-- ── Search & filter chips ──────────────────────────────────────────── -->
    <section class="card">
        <h2>Search &amp; Filter chips</h2>
        <div class="card-body">
            <SearchInput label="Search entries" bind:value={searchValue} placeholder="Search entries…" />
            <FilterChips
                options={pillOptions}
                active={activePills}
                label="Filter:"
                on:toggle={e => togglePill(e.detail)}
            />
            {#if searchValue || activePills.size > 0}
                <p class="demo-hint">Query: "{searchValue}" · Active: {[...activePills].join(', ') || 'none'}</p>
            {/if}
        </div>
    </section>

    <!-- ── Buttons ────────────────────────────────────────────────────────── -->
    <section class="card">
        <h2>Buttons</h2>
        <div class="card-body">
            <div class="card-group">
                <p class="field-label">Filled (Contained)</p>
                <div class="button-row">
                    <Button variant="primary">Primary</Button>
                    <Button variant="danger">Danger</Button>
                    <Button variant="success">Success</Button>
                    <Button variant="warning">Warning</Button>
                    <Button mode="filled">Default</Button>
                </div>
            </div>

            <div class="card-group">
                <p class="field-label">Outlined</p>
                <div class="button-row">
                    <Button mode="outlined" variant="primary">Primary</Button>
                    <Button mode="outlined" variant="danger">Danger</Button>
                    <Button mode="outlined">Default</Button>
                    <Button variant="page">← Prev</Button>
                    <Button variant="page">Next →</Button>
                </div>
            </div>

            <div class="card-group">
                <p class="field-label">Text (Ghost)</p>
                <div class="button-row">
                    <Button variant="primary" mode="text">Primary</Button>
                    <Button variant="danger"  mode="text">Danger</Button>
                    <Button>Default</Button>
                </div>
            </div>

            <div class="card-group">
                <p class="field-label">Icon</p>
                <div class="button-row">
                    <Button variant="icon" title="Download">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                            <polyline points="7 10 12 15 17 10"/>
                            <line x1="12" y1="15" x2="12" y2="3"/>
                        </svg>
                    </Button>
                    <Button variant="icon" title="Settings">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                        </svg>
                    </Button>
                </div>
            </div>

            <div class="card-group">
                <p class="field-label">Disabled</p>
                <div class="button-row">
                    <Button variant="primary" disabled>Primary</Button>
                    <Button variant="danger"  disabled>Danger</Button>
                    <Button disabled>Default</Button>
                </div>
            </div>
        </div>
    </section>

    <!-- ── Identity / auth ────────────────────────────────────────────────── -->
    <section class="card">
        <h2>Identity &amp; auth</h2>
        <div class="card-body">
            <div class="field-grid">
                <PasswordInput {errors} name="f-pw"  label="Password"         required showStrength bind:value={password} />
                <PasswordInput {errors} name="f-pw2" label="Confirm password" required matchValue={password} />
            </div>
            <PinInput {errors} name="f-pin" label="PIN code" length={6} required />
        </div>
    </section>

    <!-- ── Host &amp; network extras ──────────────────────────────────────── -->
    <section class="card">
        <h2>Host &amp; network extras</h2>
        <div class="field-grid">
            <MacInput      {errors} name="f-mac"      label="MAC address" />
            <HostnameInput {errors} name="f-hostname" label="Hostname" />
            <PortInput     {errors} name="f-portnum"  label="Port" value={443} />
        </div>
    </section>

    <!-- ── Advanced inputs ────────────────────────────────────────────────── -->
    <section class="card">
        <h2>Advanced inputs</h2>
        <div class="field-grid">
            <NumberStepper {errors} name="f-stepper"  label="Timeout"  value={30} min={1} max={300} unit="s" />
            <DurationInput {errors} name="f-duration" label="Interval" hours={0} minutes={5} seconds={0} />
            <ComboBox      {errors} name="f-combo"    label="OS" options={comboOptions} placeholder="Search OS…" />
        </div>
        <TagsInput {errors} name="f-tags" label="Allowed hosts" tags={allowedHosts} placeholder="Add host or IP…" />
    </section>
</div>

<style lang="scss">
    // ── Validation banner ─────────────────────────────────────────────────────

    .validation-banner {
        display: flex;
        align-items: center;
        gap: 0.5em;
        padding: 0.6em 1em;
        margin-bottom: 1.25em;
        background-color: rgba(244, 67, 54, 0.1);
        border: 1px solid $color-status-error;
        border-radius: 6px;
        color: $color-status-error;
        font-size: 0.9em;
    }

    // ── Layouts ───────────────────────────────────────────────────────────────

    .field-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
        gap: 1em 1.5em;
    }

    .check-group {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5em 2em;
        align-items: center;
    }

    // ── Buttons ───────────────────────────────────────────────────────────────

    .button-row {
        display: flex;
        flex-wrap: wrap;
        gap: 0.75rem;
        align-items: center;
        margin-bottom: 0.5rem;
    }

    .field-label {
        font-size: 0.75em;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: $color-text-muted;
    }

    .demo-hint {
        font-size: 0.8em;
        color: $color-text-muted;
    }

</style>
